import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { OverflowNode } from '@lexical/overflow';
import type { InitialConfigType } from "@lexical/react/LexicalComposer";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { ParagraphNode, TextNode } from "lexical";
import { CustomHeadingNode } from "./nodes/customHeadingNode";
import { CustomLinkNode } from "./nodes/customLinkNode";
import { CustomParagraphNode } from "./nodes/customParagraphNode";
import { HorizontalRuleNode } from "./nodes/horizontalRuleNode";
import { InlineImageNode } from './nodes/InlineImageNode';

import editorTheme from "./themes/editorTheme";

import {
	type DOMExportOutput,
	type DOMExportOutputMap,
	isHTMLElement,
	type Klass,
	type LexicalEditor,
	type LexicalNode,
} from 'lexical';

const removeStylesExportDOM = (
	editor: LexicalEditor,
	target: LexicalNode,
): DOMExportOutput => {
	const output = target.exportDOM(editor);

	if (output && isHTMLElement(output.element)) {
		// Remove all inline styles and classes if the element is an HTMLElement
		// Children are checked as well since TextNode can be nested
		// in i, b, and strong tags.
		for (const el of [
			output.element,
			...output.element.querySelectorAll('[dir]'),
		]) {
			el.removeAttribute('dir');
		}

		for (const el of [
			output.element,
			...output.element.querySelectorAll('[style]'),
		]) {
			el.style.removeProperty('white-space');
			el.style.removeProperty('text-wrap-mode');
			if (el.style.length === 0) {
				el.removeAttribute('style');
			}
		}
	}
	return output;
};

const exportMap: DOMExportOutputMap = new Map<
	Klass<LexicalNode>,
	(editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
	[CustomHeadingNode, removeStylesExportDOM],
	[CustomParagraphNode, removeStylesExportDOM],
	[CustomLinkNode, removeStylesExportDOM],
	[HeadingNode, removeStylesExportDOM],
	[ParagraphNode, removeStylesExportDOM],
	[LinkNode, removeStylesExportDOM],
	[TextNode, removeStylesExportDOM],
]);

export const editorConfig: InitialConfigType = {
	html: {
		export: exportMap,
	},
	namespace: '',
	theme: editorTheme,
	onError(error) {
		throw error;
	},
	nodes: [
		ListNode,
		ListItemNode,
		QuoteNode,
		AutoLinkNode,
		OverflowNode,
		HorizontalRuleNode,
		InlineImageNode,
		CustomParagraphNode,
		CustomLinkNode,
		CustomHeadingNode,
		{
			replace: HeadingNode,
			with: (node: HeadingNode) => {
				return new CustomHeadingNode(node.__tag, '');
			},
			withKlass: CustomHeadingNode,
		},
		{
			replace: ParagraphNode,
			with: (node: ParagraphNode) => {
				//console.log(node.key, node.getKey(), node.__key);
				return new CustomParagraphNode('');
			},
			withKlass: CustomParagraphNode,
		},
		{
			replace: LinkNode,
			with: (node: LinkNode) => {
				//return new LinkNode(node.__url, {rel: node.__rel, target: node.__target, title: node.__title}, node.getKey());
				return new CustomLinkNode(node.__url, {rel: node.__rel, target: node.__target, title: node.__title}, '');
			},
			withKlass: CustomLinkNode,
		}
	]
};
